import React, { useState } from 'react'
import { Box, Typography, SxProps, Theme } from '@mui/material'
import { PartnerPageData } from 'types/types'
import { useRoundPositionData, useGameToggleData, useRoundWinner } from 'hooks/useGamePositionData'
import Link from 'next/link'

interface CurrentRoundProps {
  gameId: number
  roundId?: number
  isUserJoined: boolean
  label?: string
  pageTheme?: PartnerPageData | null
  textColor?: string
  showTotalPoints?: boolean
  showWinner?: boolean
  missedRound?: boolean
  isFinished?: boolean
  enableRoundToggle?: boolean
  isHorseRacing?: boolean
  sx?: SxProps<Theme>
  gamePosition?: number | null
  gameScore?: number | null
  gameWinnerName?: string | null
  gameWinnerScore?: number | null
  winnerRoundId?: number
}

function getOrdinal(position: number): string {
  const remainder100 = position % 100
  const remainder10 = position % 10
  const formatted = position.toLocaleString('en-GB')
  if (remainder100 >= 11 && remainder100 <= 13) return `${formatted}th`
  if (remainder10 === 1) return `${formatted}st`
  if (remainder10 === 2) return `${formatted}nd`
  if (remainder10 === 3) return `${formatted}rd`
  return `${formatted}th`
}

function formatScore(score: number, isHorseRacing?: boolean): string {
  return `${score.toLocaleString('en-GB', {
    minimumFractionDigits: isHorseRacing ? 2 : 0,
    maximumFractionDigits: isHorseRacing ? 2 : 0,
  })} pts`
}
function CurrentUserPosition({
  gameId,
  roundId,
  isUserJoined,
  pageTheme,
  textColor,
  label,
  showTotalPoints,
  showWinner,
  missedRound,
  isFinished,
  enableRoundToggle,
  isHorseRacing,
  sx,
  gamePosition,
  gameScore,
  gameWinnerName,
  gameWinnerScore,
  winnerRoundId,
}: CurrentRoundProps) {
  const [viewMode, setViewMode] = useState<'game' | 'round'>('round')
  const needsRoundLeaderboard = !!roundId && (!isUserJoined || !!showWinner)
  const { roundPositionData, roundWinner, roundWinnerScore, isRoundPositionTied } =
    useRoundPositionData(gameId, roundId, needsRoundLeaderboard)

  const needsWinnerRound = !!winnerRoundId && (!isUserJoined || !!showWinner)
  const { winner: fetchedRoundWinner, winnerScore: fetchedRoundWinnerScore } = useRoundWinner(
    gameId,
    winnerRoundId,
    needsWinnerRound,
  )

  const showToggle = !!enableRoundToggle && roundId != null
  let effectiveRoundId = roundId
  if (showToggle) {
    effectiveRoundId = viewMode === 'round' ? roundId : undefined
  }

  const isOverallView = showToggle && viewMode === 'game'
  const needsGameLeaderboard = isOverallView && (!isUserJoined || !!showWinner)
  const {
    gamePositionData,
    gameWinner: fetchedGameWinner,
    gameWinnerScore: fetchedGameWinnerScore,
    isGamePositionTied,
  } = useGameToggleData(gameId, isOverallView, needsGameLeaderboard)

  const batchedPositionData =
    gamePosition != null ? { position: gamePosition, score: gameScore ?? null } : undefined

  let positionData: { position: number; score: number | null } | undefined
  let winner: string | null
  let winnerScore: number | null
  let isTied: boolean

  if (effectiveRoundId) {
    positionData = roundPositionData
    winner = roundWinner
    winnerScore = roundWinnerScore
    isTied = isRoundPositionTied
  } else if (isOverallView) {
    positionData = gamePositionData ?? batchedPositionData
    winner = needsGameLeaderboard
      ? fetchedGameWinner ?? gameWinnerName ?? null
      : gameWinnerName ?? null
    winnerScore = needsGameLeaderboard
      ? fetchedGameWinnerScore ?? gameWinnerScore ?? null
      : gameWinnerScore ?? null
    isTied = isGamePositionTied
  } else {
    positionData = batchedPositionData
    winner = needsWinnerRound
      ? fetchedRoundWinner ?? gameWinnerName ?? null
      : gameWinnerName ?? null
    winnerScore = needsWinnerRound
      ? fetchedRoundWinnerScore ?? gameWinnerScore ?? null
      : gameWinnerScore ?? null
    isTied = false
  }

  const hasWinner = !!winner

  const amberText = '#D97706'
  const darkAmberText = '#92400e'
  const grayText = '#6B7280'

  if (!isUserJoined) {
    const hasNothingToShow = !hasWinner && !missedRound
    if (hasNothingToShow) return null

    return (
      <Box display="flex" flexDirection="column" gap={1} p={1.25} sx={sx}>
        {missedRound && (
          <Box
            bgcolor="#FFF8E5"
            border="1px solid #FFDF86"
            borderRadius="6px"
            px={2}
            py={2.5}
            textAlign="center"
          >
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: darkAmberText, mb: 0.8 }}>
              You missed this round but you are still in the game
            </Typography>
            <Link
              href={`/${pageTheme?.company_domain}/games/${gameId}`}
              style={{ textDecoration: 'none' }}
            >
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: amberText,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Check your position now
              </Typography>
            </Link>
          </Box>
        )}

        {hasWinner && (
          <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.25}>
            <Typography sx={{ fontSize: '0.9rem', color: 'text.primary' }}>Winner</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: grayText }}>
              {winner}
              {winnerScore != null ? ` — ${formatScore(winnerScore, isHorseRacing)}` : ''}
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  let positionLabel = '-'
  if (positionData?.position != null) {
    positionLabel =
      isFinished && isTied
        ? `Tied - ${getOrdinal(positionData.position)}`
        : getOrdinal(positionData.position)
  }

  const accentColor = textColor ?? grayText

  return (
    <Box
      borderRadius="6px"
      p={1.5}
      display="flex"
      flexDirection="column"
      gap={1}
      sx={{ bgcolor: 'grey.100', border: `1px solid ${grayText}`, ...sx }}
    >
      {positionData && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {showToggle ? (
            <Box display="flex" alignItems="center" gap={0.6}>
              <Box
                role="group"
                aria-label="Position view"
                display="inline-flex"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.06)',
                  borderRadius: '999px',
                  p: '2px',
                }}
              >
                {(['round', 'game'] as const).map((mode) => (
                  <Box
                    key={mode}
                    component="button"
                    type="button"
                    aria-pressed={viewMode === mode}
                    onClick={() => setViewMode(mode)}
                    sx={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      border: 'none',
                      outline: 'none',
                      font: 'inherit',
                      px: 1,
                      borderRadius: '999px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      lineHeight: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition:
                        'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                      color: viewMode === mode ? '#fff' : accentColor,
                      bgcolor: viewMode === mode ? accentColor : 'transparent',
                      boxShadow: viewMode === mode ? '0 1px 2px rgba(0,0,0,0.15)' : 'none',
                      '&:focus-visible': {
                        outline: `2px solid ${accentColor}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    {mode === 'round' ? 'Round' : 'Overall'}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: '0.875rem', color: pageTheme?.primary_text_color }}>
                Position
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.875rem', color: pageTheme?.primary_text_color }}>
              {label ?? 'Current Position'}
            </Typography>
          )}
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: accentColor }}>
            {positionLabel}
          </Typography>
        </Box>
      )}
      {showTotalPoints && positionData && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: '0.875rem', color: pageTheme?.primary_text_color }}>
            Total Points
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: textColor ?? grayText }}>
            {positionData.score != null ? formatScore(positionData.score, isHorseRacing) : '-'}
          </Typography>
        </Box>
      )}
      {showWinner && hasWinner && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: '0.875rem', color: pageTheme?.primary_text_color }}>
            Winner
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: textColor ?? grayText }}>
            {winner}
            {winnerScore != null ? ` — ${formatScore(winnerScore, isHorseRacing)}` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CurrentUserPosition
